// src/components/ui/Button.tsx
import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'success' | 'warning' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantClasses = {
    primary: 'bg-teal-main text-white hover:bg-teal-light focus:ring-teal-light shadow-md hover:shadow-lg',
    secondary: 'border-2 border-teal-main text-teal-main bg-white hover:bg-teal-main hover:text-white focus:ring-teal-main',
    outline: 'border border-neutral-border text-neutral-text-primary bg-white hover:bg-neutral-bg focus:ring-neutral-border',
    danger: 'bg-danger text-white hover:bg-red-600 focus:ring-danger shadow-md',
    success: 'bg-success text-white hover:bg-green-600 focus:ring-success shadow-md',
    warning: 'bg-warning text-white hover:bg-yellow-600 focus:ring-warning shadow-md',
    ghost: 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-neutral-text-primary focus:ring-neutral-border',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-6 py-3 text-base gap-2.5',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      {children}
    </button>
  );
};

export default Button;